import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "../../api/client";
import { enrollFromFile, toArray } from "../../auth/face";
import { useMe } from "../../auth/useMe";
import { AssignmentManager } from "./AssignmentManager";
import { RbacApi, type AppUser } from "./api";

export function Users() {
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [error, setError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [managing, setManaging] = useState<string | null>(null);

  const users = useQuery({ queryKey: ["users"], queryFn: RbacApi.listUsers });
  const deactivate = useMutation({
    mutationFn: RbacApi.deactivateUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const reactivate = useMutation({
    mutationFn: RbacApi.reactivateUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
  const reset = useMutation({
    mutationFn: RbacApi.resetPassword,
    onSuccess: (r) => setInviteLink(r.passwordResetLink),
  });
  const grantSa = useMutation({
    mutationFn: RbacApi.grantSuperadmin,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const revokeSa = useMutation({
    mutationFn: RbacApi.revokeSuperadmin,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  return (
    <div className="hierarchy">
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h1>Users</h1>
          <p className="muted">
            Create accounts and manage who can use the platform. The Create flow assigns a role + scope at the same time.
            To change someone's roles or scopes later, click <strong>Edit access</strong> on their row.
          </p>
        </div>
        <Link to="new"><button>+ Create user</button></Link>
      </header>
      {error && <div role="alert" className="error">{error}</div>}
      {users.error && (
        <div role="alert" className="error">
          Couldn't load users: {users.error instanceof ApiError
            ? `HTTP ${users.error.status} — ${users.error.message}`
            : (users.error as Error).message}
        </div>
      )}
      {inviteLink && (
        <div className="info">
          Password reset link — share with the user. They click it to set a new password:
          <input readOnly value={inviteLink} onFocus={(e) => e.target.select()} style={{ width: "100%", marginTop: 6 }} />
        </div>
      )}

      {users.isLoading && <div className="empty">Loading users…</div>}
      {users.data?.items.length === 0 && (
        <div className="empty">No users yet. Invite someone above.</div>
      )}

      <ul className="tree">
        {(users.data?.items ?? []).map((u) => (
          <li key={u.id}>
            <div className="row">
              <span>
                <strong>{u.fullName || u.email}</strong> <span className="muted">({u.email})</span>
                {u.isSuperadmin && (
                  <span className="badge danger" style={{ marginLeft: 8 }}>Superadmin</span>
                )}
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                  {u.status} · {(u.permissions ?? []).length} effective permission(s)
                  {" · "}Face {u.mfaEnrolled ? "enrolled" : "not set"}
                </div>
              </span>
              <span className="row-actions">
                <button
                  className="ghost"
                  onClick={() => setManaging(managing === u.id ? null : u.id)}
                >
                  {managing === u.id ? "Close" : "Edit access"}
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Generate a password-reset link for ${u.fullName || u.email}? Their current password keeps working until they use the new link.`))
                      reset.mutate(u.id);
                  }}
                >Reset password</button>
                {isSuperadmin && (
                  <SetFaceButton
                    user={u}
                    onError={setError}
                    onDone={() => qc.invalidateQueries({ queryKey: ["users"] })}
                  />
                )}
                {isSuperadmin && u.mfaEnrolled && (
                  <ViewFaceButton user={u} onError={setError} />
                )}
                {u.isSuperadmin ? (
                  <button
                    className="danger"
                    onClick={() => {
                      if (confirm(`Revoke superadmin from ${u.fullName}? This signs them out and they lose all elevated access.`))
                        revokeSa.mutate(u.id);
                    }}
                  >Revoke superadmin</button>
                ) : (
                  <button
                    onClick={() => {
                      if (confirm(`Grant superadmin to ${u.fullName}? They will bypass every permission and scope check across the platform.`))
                        grantSa.mutate(u.id);
                    }}
                  >Grant superadmin</button>
                )}
                {u.status === "active" ? (
                  <button
                    className="danger"
                    onClick={() => {
                      if (confirm(`Deactivate ${u.fullName || u.email}? They'll be signed out and locked out until reactivated. Their records stay.`))
                        deactivate.mutate(u.id);
                    }}
                  >Deactivate</button>
                ) : (
                  <button
                    onClick={() => {
                      if (confirm(`Reactivate ${u.fullName || u.email}? They'll be able to sign in again.`))
                        reactivate.mutate(u.id);
                    }}
                  >Reactivate</button>
                )}
              </span>
            </div>
            {managing === u.id && <AssignmentManager userId={u.id} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Superadmin-only: attach or replace an officer's enrolled face for the
 * sign-in second factor. The browser computes the face descriptor (for
 * matching) and sends it with the photo, which the server encrypts and stores.
 * The server enforces superadmin too (PUT /users/<uid>/face).
 */
function SetFaceButton({
  user, onError, onDone,
}: {
  user: AppUser;
  onError: (m: string) => void;
  onDone: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file after an error
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      onError("Please choose an image file.");
      return;
    }
    setBusy(true);
    setDone(false);
    onError("");
    try {
      const { descriptor, image, contentType } = await enrollFromFile(file);
      if (!descriptor) {
        onError(`No clear single face found in that photo for ${user.fullName || user.email}. Use a front-facing, well-lit photo.`);
        return;
      }
      await RbacApi.setUserFace(user.id, {
        faceDescriptor: toArray(descriptor),
        faceImage: image,
        faceImageContentType: contentType,
      });
      setDone(true);
      onDone();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const label = busy ? "Reading…" : done ? "✓ Face set" : user.mfaEnrolled ? "Replace face" : "Set face";
  return (
    <>
      <button className="ghost" onClick={() => inputRef.current?.click()} disabled={busy}>
        {label}
      </button>
      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} />
    </>
  );
}

/**
 * Superadmin-only: view an officer's enrolled photo. Calls the gated endpoint
 * (GET /users/<uid>/face/image), which decrypts server-side. The image lives
 * only in component state for the lifetime of the modal — never cached.
 */
function ViewFaceButton({ user, onError }: { user: AppUser; onError: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [src, setSrc] = useState<string | null>(null);

  async function open() {
    setBusy(true);
    onError("");
    try {
      const { dataUrl } = await RbacApi.getUserFaceImage(user.id);
      setSrc(dataUrl);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        onError(`No photo stored for ${user.fullName || user.email} — descriptor only.`);
      } else {
        onError(err instanceof ApiError ? err.message : (err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button className="ghost" onClick={open} disabled={busy}>
        {busy ? "Loading…" : "View photo"}
      </button>
      {src && (
        <div className="face-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setSrc(null)}>
          <div className="face-modal" onClick={(e) => e.stopPropagation()}>
            <img src={src} alt={`Enrolled face for ${user.fullName || user.email}`} />
            <div className="face-modal-footer">
              <span className="muted" style={{ fontSize: 12 }}>
                {user.fullName || user.email} · decrypted server-side, not cached
              </span>
              <button onClick={() => setSrc(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
